import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router-dom'
import { IconButton, Popover, Tooltip } from '@material-ui/core'
import { FiBell } from 'react-icons/fi'
import { FaBell } from 'react-icons/fa'

import { useInboxNotifications } from '../../customHooks/useInboxNotifications'
import NotificationFeedItem from '../../views/Notifications/components/NotificationFeedItem'
import { getNavPath } from '../../views/Notifications/notifications.adapter'
import { isSuperAdminUser } from '../../views/Dashboard/saasAdmin.utils'
import paths from '../../routes/paths'
import WebPushToggle from '../../views/Notifications/components/WebPushToggle'

export default function HeaderNotifications({ user }) {
  const { formatMessage: trans } = useIntl()
  const history = useHistory()
  const isSuperAdmin = isSuperAdminUser(user)
  const [anchorEl, setAnchorEl] = React.useState(null)
  const open = Boolean(anchorEl)

  const { unreadCount, items, isFetchingList, fetchList, markOneRead, markAllRead } = useInboxNotifications()

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget)
    fetchList()
  }
  const handleClose = () => setAnchorEl(null)

  const handleMarkAllRead = useCallback(() => {
    markAllRead()
  }, [markAllRead])

  const handleRead = useCallback(
    (item) => {
      markOneRead(item.id)
      handleClose()
      history.push(getNavPath(item.dataType, item.dataId, item.eventType, { isSuperAdmin }))
    },
    [markOneRead, history, isSuperAdmin],
  )

  const goSeeAll = () => {
    handleClose()
    history.push(paths.notifications)
  }

  const goAdminHistory = () => {
    handleClose()
    history.push(paths.admin.pushNotificationsHistory)
  }

  return (
    <>
      <Tooltip title={trans({ id: 'header_notifications_title', defaultMessage: 'Notifications' })} arrow>
        <IconButton
          onClick={handleOpen}
          color="inherit"
          className="p-1 position-relative header-notifications-trigger"
          aria-label={trans({ id: 'header_notifications_title', defaultMessage: 'Notifications' })}
          aria-expanded={open}
          aria-haspopup="dialog"
        >
          <FiBell style={{ fontSize: '1.25rem', color: '#64748b' }} />
          {unreadCount > 0 ? (
            <span className="notifications__count" aria-hidden>
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          ) : null}
        </IconButton>
      </Tooltip>
      <Popover
        id="header-notifications-panel"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ className: 'header-notifications-popover' }}
        marginThreshold={8}
      >
        <div className="header-notifications-panel" role="dialog" aria-label={trans({ id: 'header_notifications_title', defaultMessage: 'Notifications' })}>
          <div className="header-notifications-panel__head">
            <div>
              <h2 className="header-notifications-panel__title">{trans({ id: 'header_notifications_title', defaultMessage: 'Notifications' })}</h2>
              {unreadCount > 0 ? <p className="header-notifications-panel__subtitle">{trans({ id: 'header_notifications_unread_count', defaultMessage: '{count} unread' }, { count: unreadCount })}</p> : null}
            </div>
            {unreadCount > 0 ? (
              <button type="button" className="header-notifications-panel__mark-read" onClick={handleMarkAllRead}>
                {trans({ id: 'header_notifications_mark_all_read', defaultMessage: 'Mark all read' })}
              </button>
            ) : null}
          </div>
          <div className="header-notifications-panel__body">
            {isFetchingList && items.length === 0 ? (
              <div className="header-notifications-empty">
                <span className="header-notifications-empty__icon">
                  <FaBell aria-hidden />
                </span>
                <p className="header-notifications-empty__desc">{trans({ id: 'loading', defaultMessage: 'Loading…' })}</p>
              </div>
            ) : items.length === 0 ? (
              <div className="header-notifications-empty">
                <span className="header-notifications-empty__icon">
                  <FaBell aria-hidden />
                </span>
                <p className="header-notifications-empty__title">{trans({ id: 'header_notifications_empty_title', defaultMessage: 'All caught up' })}</p>
                <p className="header-notifications-empty__desc">{trans({ id: 'header_notifications_empty', defaultMessage: 'New alerts will show up here.' })}</p>
              </div>
            ) : (
              <div className="header-notifications-list">
                {items.map((item) => (
                  <NotificationFeedItem key={item.id} item={item} onClick={handleRead} variant="compact" />
                ))}
              </div>
            )}
          </div>
          <WebPushToggle compact />
          <div className="header-notifications-panel__foot">
            <button type="button" className="header-notifications-panel__view-all" onClick={goSeeAll}>
              {trans({ id: 'header_notifications_see_all', defaultMessage: 'See all notifications' })}
            </button>
            {user?.super_admin ? (
              <button type="button" className="header-notifications-panel__view-all" onClick={goAdminHistory}>
                {trans({ id: 'header_notifications_view_admin', defaultMessage: 'Manage push notifications' })}
              </button>
            ) : null}
          </div>
        </div>
      </Popover>
    </>
  )
}
